import { formatDate as ngFormatDate, formatNumber as ngFormatNumber } from '@angular/common';
import moment from 'moment';
import { UserSettings } from '../user-settings/user-settings.class';
import { defaultTxt } from './strings.constants';

const MASKING_TOKEN = '*****';

export interface MiqSanitizers {
  Number: Function;
  Boolean: Function;
  Date: Function;
  formatNumber: Function;
  isMasked: Function;
  MiqMoment: any;
}

/**
 * Returns whether value is equal to the masked wildcard.
 * Security: uses constant-time comparison to prevent timing side-channel
 * attacks that could reveal the masking token value.
 */
const isMasked = (x: unknown): boolean => {
  const a = String(x);
  const b = MASKING_TOKEN;
  if (a.length !== b.length) return false;
  let result = 0;
  for (let i = 0; i < b.length; i++) {
    result |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return result === 0;
};

/**
 * Attempts conversion only if value is not the masked value
 * @param x: any value, intention is to receive numeric values
 *
 * @returns Number value OR mask wildcard value
 */
const MiqNumber = x => (isMasked(x) ? x : Number(x));

/**
 * Wrapper function for @angular/common/formatNumber but before using angular we ask return masked
 * value so we don't fall in errors trying to convert wildcard '*****'into a number
 *
 * @param val: numeric value to format OR '*****' wildcard
 * @param locale A locale code for the locale format rules to use.
 * @param digits Decimal representation options, specified by a string in the following format:
 * @param defaultText Default text value to return if val us undefined or null
 *
 * @returns The formatted text string.'en-US', '.0-2'
 * @see [Internationalization (i18n) Guide](https://angular.io/guide/i18n)
 */
function MiqFormatNumber(val, locale = 'en-US', digits = '.0-2', defaultText = defaultTxt): string {
  let result: string;
  const isParsable = val != null && !Number.isNaN(Number(val));
  if (val != null && (isMasked(val) || !isParsable)) {
    result = val as string;
  } else {
    result = val == null ? defaultText : ngFormatNumber(Number(val), locale, digits);
  }
  return result;
}

/**
 * Wrapper function for @angular/common/i18/formatDate but before using angular we ask return masked
 * value so we don't fall in errors trying to convert wildcard '*****'into a date
 * @param val
 * @param format
 * @param locale
 * @param timezone
 */
const MiqFormatDate = (val: string | number | Date, format: string, locale: string, timezone: string = undefined) => {
  if (isMasked(val)) return val;

  try {
    return ngFormatDate(val, format, locale, timezone);
  } catch {
    return 'Invalid date';
  }
};

/**
 * Attempts conversion only if value is not the masked value
 * @param x: Boolean value, intention is to receive Boolean values
 *
 * @returns Boolean object OR mask wildcard value
 */
const MiqBool = x => (isMasked(x) ? x : Boolean(x));

/**
 * It returns masked if masked, otherwise converts to boolean and returns formatted text for each value
 * @example
 * boolToString(false, 'foo', 'bar'); // 'bar'
 * boolToString(true); // 'Yes'
 * boolToString('*****'); // '*****'
 *
 * @param value Intended value to be converted to boolean
 * @param yesText Text to be used for truthy values (default 'Yes')
 * @param noText Text to be used for falsy values (default 'No)
 * @param emptyText Text to be used for empty value
 *
 * @returns string
 */
const boolToString = (value: any, yesText = 'Y', noText = 'N', emptyText = '') => {
  return isMasked(value) || ['', null].includes(value) ? emptyText : Boolean(value) ? yesText : noText;
};

/**
 * Receives a date string and returns a formatted value
 * @param d, a date string
 * @param fmt (OPTIONAL), format to be returned, DEFAULTED TO 'MM-DD-YYYY'
 *
 * @returns formatted date
 */
const momentFormat = (d, fmt = 'M/D/YYYY') => (isMasked(d) ? d : moment(d).utc().format(fmt));

/**
 * It uses an specific input format and produces a different date format
 * @param d: Date value or wildcard
 * @param inFmt: specific date format, it specifies how to read the date
 * @param outFmt: the output date format to apply
 * @param toUtc Flag to convert date to UTC
 *
 * @returns formatted date string
 */
const momentInputFormat = (
  d,
  inFmt: string,
  outFmt = UserSettings.getGlobalDateDisplayFormat('value'),
  toUtc = true
) => (isMasked(d) ? d : toUtc ? moment(d, inFmt).utc().format(outFmt) : moment(d).format(outFmt));

/**
 * Returns a built in formatted string from moment.fromNow function e.g '3 minutes ago'
 * @param t, a date string
 *
 * @returns fromNow moment value
 */
const fromNow = t => (isMasked(t) ? t : moment(t).fromNow());

const MiqMoment = {
  fromNow,
  format: momentFormat,
  formatFromInputFormat: momentInputFormat,
  utcInputFormat: `${moment.HTML5_FMT.DATETIME_LOCAL}Z`, // this is the resulting value => 'YYYY-MM-DDTHH:mmZ'
  utcOutputFormat: 'YYYY-MM-DDTHH:mm:ss[Z]', // this is the resulting value => 'YYYY-MM-DDTHH:mmZ'
};

export {
  MiqNumber,
  MiqBool,
  boolToString,
  MiqFormatNumber as formatNumber,
  MiqFormatDate as formatDate,
  isMasked,
  MiqMoment,
};
